import DOMPurify from 'dompurify'

/**
 * Default configuration for plain text sanitization
 * Removes all HTML tags
 */
const PLAIN_TEXT_CONFIG = {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
}

/**
 * Configuration for rendered Markdown content sanitization
 * Allows only safe tags
 */
const MARKDOWN_CONFIG = {
    ALLOWED_TAGS: [
        'b', 'i', 'em', 'strong', 'a', 'p', 'br',
        'ul', 'ol', 'li', 'code', 'pre', 'blockquote',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr',
        'img', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
        'span', 'div', 'section', 'article'
    ],
    ALLOWED_ATTR: [
        'href', 'title', 'alt', 'src', 'class', 'id',
        'target', 'rel', 'align', 'style'
    ],
    KEEP_CONTENT: true,
    FORCE_BODY: false
}

/**
 * Sanitizes plain text by removing any HTML
 * @param {string} text - The text to be sanitized
 * @returns {string} Sanitized text
 */
export const sanitizeText = (text) => {
    if (!text || typeof text !== 'string') return ''
    return DOMPurify.sanitize(text, PLAIN_TEXT_CONFIG)
}

/**
 * Sanitizes HTML/Markdown content while keeping safe tags
 * @param {string} html - The HTML to be sanitized
 * @returns {string} Sanitized HTML
 */
export const sanitizeHtml = (html) => {
    if (!html || typeof html !== 'string') return ''
    return DOMPurify.sanitize(html, MARKDOWN_CONFIG)
}

/**
 * Validates and sanitizes URLs to prevent malicious javascript: and data: URIs
 * @param {string} url - The URL to be validated
 * @returns {string|null} Safe URL or null if dangerous
 */
export const sanitizeUrl = (url) => {
    if (!url || typeof url !== 'string') return null
    
    // Trim whitespace
    const trimmedUrl = url.trim()
    
    // Block dangerous protocols
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:']
    if (dangerousProtocols.some(proto => trimmedUrl.toLowerCase().startsWith(proto))) {
        console.warn(`[Security] Blocked dangerous URL protocol: ${url}`)
        return null
    }
    
    // If it's a relative URL, HTTP(S), or browser-created object URL, it is considered safe
    if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://') || trimmedUrl.startsWith('blob:')) {
        return trimmedUrl
    }
    
    // Block other potentially dangerous URLs
    console.warn(`[Security] Blocked suspicious URL: ${url}`)
    return null
}

/**
 * Sanitizes image attributes
 * @param {Object} imgProps - Image properties
 * @returns {Object} Sanitized properties
 */
export const sanitizeImageProps = (imgProps) => {
    if (!imgProps || typeof imgProps !== 'object') return {}
    
    const { src, alt, title, width, height, loading, decoding, ...rest } = imgProps
    
    return {
        src: sanitizeUrl(src) || '',
        alt: sanitizeText(alt) || '',
        title: sanitizeText(title) || '',
        width: typeof width === 'number' ? width : undefined,
        height: typeof height === 'number' ? height : undefined,
        loading: loading === 'lazy' ? 'lazy' : undefined,
        decoding: decoding === 'async' ? 'async' : undefined,
        // Remove any dangerous custom attributes
        ...Object.fromEntries(
            Object.entries(rest)
                .filter(([key]) => !key.startsWith('on') && !key.includes('dangerouslySet'))
        )
    }
}

/**
 * Sanitizes link attributes
 * @param {Object} linkProps - Link properties
 * @returns {Object} Sanitized properties
 */
export const sanitizeLinkProps = (linkProps) => {
    if (!linkProps || typeof linkProps !== 'object') return {}
    
    const { href, target, title, ...rest } = linkProps

    return {
        href: sanitizeUrl(href) || '#',
        target: target === '_blank' ? '_blank' : undefined,
        rel: target === '_blank' ? 'noopener noreferrer' : undefined,
        title: sanitizeText(title) || '',
        // Remove event handlers (and the rel of the caller: it is set above)
        ...Object.fromEntries(
            Object.entries(rest)
                .filter(([key]) => key !== 'rel' && !key.startsWith('on') && !key.includes('dangerouslySet'))
        )
    }
}

/**
 * Validates if a string contains potential XSS patterns
 * @param {string} str - String to validate
 * @returns {boolean} true if it contains suspicious patterns
 */
export const containsPotentialXSS = (str) => {
    if (!str || typeof str !== 'string') return false
    
    const xssPatterns = [
        /<script[^>]*>.*?<\/script>/gi,
        /on\w+\s*=\s*['"]/gi,
        /javascript:/gi,
        /vbscript:/gi,
        /<iframe[^>]*>/gi,
        /<object[^>]*>/gi,
        /<embed[^>]*>/gi,
        /eval\s*\(/gi
    ]
    
    return xssPatterns.some(pattern => pattern.test(str))
}

export default {
    sanitizeText,
    sanitizeHtml,
    sanitizeUrl,
    sanitizeImageProps,
    sanitizeLinkProps,
    containsPotentialXSS
}